<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { Head, useForm } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import { platformLabel as platformName } from '@/lib/platformLabel'
import CatalogPlaceholder from '@/Components/Public/CatalogPlaceholder.vue'
import PublicGallery from '@/Components/Public/PublicGallery.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import LuxButton from '@/Components/Public/LuxButton.vue'
import BookingRequestForm from '@/Components/Public/BookingRequestForm.vue'
import CheckoutCard from '@/Components/Public/CheckoutCard.vue'
import DateField from '@/Components/Public/DateField.vue'
import ProductCard from '@/Components/Public/ProductCard.vue'
import TrustRow from '@/Components/Public/TrustRow.vue'
import PaymentMethods from '@/Components/Public/PaymentMethods.vue'
import MobileBar from '@/Components/Public/MobileBar.vue'
import { usePublicDate } from '@/composables/usePublicDate'
import { usePublicMoney } from '@/composables/usePublicMoney'
import { usePriceQuote } from '@/composables/usePriceQuote'

// Charter detail: the boat, its formats and whole-boat prices, and a
// request-to-book card (date, format, guests) whose estimate resolves per
// whole vessel (PriceQuote#quote_charter). Round-two item 16: a priced boat
// can also be booked and paid on the spot — the page owns the live server
// quote for the chosen format, date and guests, the CheckoutCard reuses it,
// and the boat's day is held for 30 minutes while the customer pays.
// Round-one item 4 + round-two items 15/17: a stat strip under the gallery,
// the covered headcount on every price row, surcharges and a safety line,
// five answered questions (also FAQPage JSON-LD), "boats like this", a
// request card that stays put on desktop and a price bar on a phone.
// Round-one item 8: reviews, moderated before display, rated on the Product node.
defineOptions({ layout: PublicLayout })

const { t, locale } = useI18n()
const { formatDate } = usePublicDate()
const { formatMoney } = usePublicMoney()
const props = defineProps({
  charter:      { type: Object, required: true },
  trip_formats: { type: Array,  default: () => [] },
  checkout_enabled: { type: Boolean, default: false },
  base_path:    { type: String, default: '/charters' },
  operated_with: { type: Object, default: null },
  destinations: { type: Array,  default: () => [] },  // [{slug, title}] the destination pages this boat is tagged for
  availability: { type: Object, default: () => ({ live: false, busy_dates: [] }) }  // partner calendar: busy ISO dates, next 12 months
})

const c = props.charter
const photos = c.photos?.length ? c.photos : []
const today = computed(() => new Date().toLocaleDateString('en-CA'))

// The request's trip_format mirrors the boat's priced formats; "custom"
// stays available so a customer can ask for something the boat does not
// list. Overnight maps to the request's multi_day (nights from the dates).
const FORMAT_TO_TRIP = { full_day: 'full_day', half_day: 'half_day', sunset: 'sunset', overnight: 'multi_day' }
const formatOptions = computed(() => {
  const listed = (c.formats || []).map(f => ({ key: f.format, trip: FORMAT_TO_TRIP[f.format], price: f.display_price, hours: f.hours, perNight: f.per_night }))
  return [...listed, { key: 'custom', trip: 'custom', price: null }]
})
const requestFormat = ref(formatOptions.value[0]?.key || 'custom')
const requestDate   = ref('')
const requestEnd    = ref('')
const requestGuests = ref(2)
const isOvernight   = computed(() => requestFormat.value === 'overnight')
const details = computed(() => ({
  start_on: requestDate.value,
  end_on: isOvernight.value ? requestEnd.value : '',
  adults: requestGuests.value,
  trip_format: FORMAT_TO_TRIP[requestFormat.value] || 'custom'
}))
const canRequest = computed(() => !!requestDate.value && (!isOvernight.value || !!requestEnd.value))

// Live availability from the partner's calendar (Teamup iCal, read
// server-side): a chosen day that is booked there blocks both the request
// and the instant checkout before anything is submitted.
const busySet = new Set(props.availability?.busy_dates || [])
const selectedBusy = computed(() => {
  if (!busySet.size || !requestDate.value) return false
  const last = isOvernight.value && requestEnd.value ? requestEnd.value : requestDate.value
  for (const d = new Date(requestDate.value + 'T12:00:00'); d.toLocaleDateString('en-CA') <= last; d.setDate(d.getDate() + 1)) {
    if (busySet.has(d.toLocaleDateString('en-CA'))) return true
  }
  return false
})
// Rendered after mount only: the list depends on today's date and the
// visitor's locale, which differ between the SSR server and the browser.
const mounted = ref(false)
onMounted(() => { mounted.value = true })
const nextBooked = computed(() => {
  const limit = new Date(); limit.setDate(limit.getDate() + 45)
  return (props.availability?.busy_dates || [])
    .filter(d => d >= today.value && d <= limit.toLocaleDateString('en-CA'))
    .slice(0, 8)
    .map(d => new Date(d + 'T12:00:00').toLocaleDateString(locale.value, { day: 'numeric', month: 'short' }))
})

// Instant booking: one live server quote for the chosen format, date and
// guests (PriceQuote#quote_charter — whole boat, nights for an overnight
// trip, extra guests above the covered headcount). The plan card shows it,
// the CheckoutCard charges exactly it. A custom trip is request-only.
const quotable = computed(() => canRequest.value && requestFormat.value !== 'custom' && !c.price_on_request)
const { quote, loading: quoteLoading, schedule: scheduleQuote } = usePriceQuote('charter', c.id)
const checkoutDetails = computed(() => ({
  start_on: requestDate.value,
  end_on: isOvernight.value ? requestEnd.value : '',
  adults: requestGuests.value,
  charter_format: requestFormat.value
}))
watch(checkoutDetails, d => scheduleQuote(quotable.value ? d : null), { immediate: true })
const totalDisplay = computed(() => (!quoteLoading.value && quote.value ? formatMoney(quote.value.total_cents) : null))

const eyebrow = computed(() => {
  const parts = [t(`public.charters.vessel_types.${c.vessel_type}`)]
  if (c.length_ft) parts.push(t('public.charters.ft', { n: c.length_ft }))
  parts.push(t('public.charters.up_to', { n: c.capacity }))
  return parts.join(' · ')
})

const chatCtas = computed(() => Object.entries(c.contact_intents || {}).map(([platform, url]) => ({ platform, url, name: platformName(platform) })))

// The four numbers a buyer asks for first, under the gallery.
const stats = computed(() => [
  c.from_price && !c.price_on_request ? { key: 'from_price', label: t('public.charters.fields.from_price'), value: c.from_price, sub: t('public.charters.per_boat') } : null,
  c.length_ft ? { key: 'length', label: t('public.charters.fields.length'), value: t('public.charters.ft', { n: c.length_ft }) } : null,
  { key: 'capacity', label: t('public.charters.fields.capacity'), value: t('public.charters.up_to', { n: c.capacity }) },
  c.cabins != null ? { key: 'cabins', label: t('public.charters.fields.cabins'), value: String(c.cabins) } : null
].filter(Boolean))

// What each price row covers: the headcount, and the extra-guest price when
// the boat charges one.
const coverLine = computed(() => {
  const parts = [t('public.charters.pricing.covers', { n: c.covers_guests || c.capacity })]
  if (c.extra_guest_price) parts.push(t('public.charters.pricing.extra', { price: c.extra_guest_price }))
  return parts.join(' · ')
})
const inclusionsLine = computed(() => (c.inclusions || []).join(' · '))

const specs = computed(() => [
  ['crew',           c.crew != null ? String(c.crew) : null],
  ['year_built',     c.year_built ? String(c.year_built) : null],
  ['home_marina',    c.home_marina ? t(`public.charters.marinas.${c.home_marina}`) : null],
  ['crew_languages', c.crew_languages?.length ? c.crew_languages.map(l => t(`public.experiences.guide_languages.${l}`)).join(', ') : null],
].filter(([, v]) => v))

const listSections = computed(() => ['highlights', 'not_included', 'surcharges', 'itineraries'].filter(k => (c[k] || []).length))

// Reviews (round-one item 8): the approved list, and the same
// submit-then-moderate form the experiences and vehicles have.
const showAllReviews = ref(false)
const visibleReviews = computed(() =>
  showAllReviews.value ? c.reviews?.items : c.reviews?.items?.slice(0, 5)
)

const reviewForm = useForm({ author_name: '', rating: 5, body: '', email: '', website: '', photos: [] })
const reviewSubmitted = ref(false)
const photoPreviews = ref([])

function onPhotoChange(e) {
  const files = Array.from(e.target.files || []).slice(0, 5)
  reviewForm.photos = files
  photoPreviews.value.forEach(u => URL.revokeObjectURL(u))
  photoPreviews.value = files.map(f => URL.createObjectURL(f))
}

function submitReview() {
  reviewForm.transform(d => ({
    review: { author_name: d.author_name, rating: d.rating, body: d.body, email: d.email, photos: d.photos },
    website: d.website
  })).post(c.reviews_path, {
    preserveScroll: true,
    forceFormData: true,
    onSuccess: () => { reviewSubmitted.value = true }
  })
}

</script>

<template>
  <Head>
    <title>{{ charter.title }} · {{ t('public.charters.head_title') }}</title>
    <meta name="description" :content="charter.summary || charter.description">
  </Head>

  <BrandHero kind="charter" :eyebrow="eyebrow" :title="charter.title">
    <template #top>
      <a :href="base_path" class="bhero-back">← {{ t('public.charters.back') }}</a>
    </template>
    <p v-if="charter.summary" class="bhero-lede">{{ charter.summary }}</p>
    <span v-if="charter.luxury || charter.tier === 'premium' || charter.tier === 'luxury'" class="bhero-vip bhero-vip--premium">{{ t('public.tiers.premium') }}</span>
    <p v-if="operated_with" class="bhero-note">{{ t('public.operated_with', operated_with) }}</p>
    <p class="bhero-note">{{ t('public.charters.route_note') }}</p>
    <p v-if="destinations.length" class="bhero-sails"><span class="bhero-sails__label">{{ t('public.charters.sails_to') }}</span><a v-for="d in destinations" :key="d.slug" :href="`${base_path.replace(/\/charters$/, '/destinations')}/${d.slug}`" class="bhero-sails__chip">{{ d.title }}</a></p>
    <div v-if="chatCtas.length" class="bhero-ctas">
      <LuxButton v-for="(cta, idx) in chatCtas" :key="cta.platform" :href="cta.url" :variant="idx === 0 ? 'solid' : 'line'" tone="dark" :glyph="cta.platform" target="_blank" rel="noopener">
        {{ t('public.write_on', { platform: cta.name }) }}
      </LuxButton>
    </div>
  </BrandHero>

  <section class="body">
    <div class="body__inner">
      <PublicGallery :photos="photos" :alt="charter.title">
        <template #placeholder>
          <CatalogPlaceholder kind="charter" />
        </template>
      </PublicGallery>

      <dl class="stats">
        <div v-for="s in stats" :key="s.key" class="stat">
          <dt class="stat__l">{{ s.label }}</dt>
          <dd class="stat__v fw-value">{{ s.value }}<small v-if="s.sub"> {{ s.sub }}</small></dd>
        </div>
      </dl>

      <div class="trust"><TrustRow /></div>

      <div class="detail">
        <div class="detail__content">
          <section v-if="charter.description" class="card">
            <h2 class="section-title">{{ t('public.charters.sections.about') }}</h2>
            <p class="prose">{{ charter.description }}</p>
          </section>

          <section class="card">
            <h2 class="section-title">{{ t('public.charters.sections.pricing') }}</h2>
            <table class="formats">
              <tbody>
                <tr v-for="f in charter.formats" :key="f.format">
                  <th scope="row">
                    {{ t(`public.charters.formats.${f.format}`) }}
                    <span class="formats__hours mono">
                      <template v-if="f.per_night">{{ t('public.charters.per_night') }}</template>
                      <template v-else-if="f.hours">{{ t('public.charters.hours', { n: f.hours }) }}</template>
                    </span>
                  </th>
                  <td class="formats__price">
                    <span class="fw-value">{{ charter.price_on_request ? t('public.vip.price_on_request') : f.display_price }}</span>
                    <span class="formats__cover">{{ coverLine }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
            <p v-if="inclusionsLine" class="incl"><b>{{ t('public.charters.pricing.inclusions_line') }}</b> {{ inclusionsLine }}</p>
            <p class="hint">{{ t('public.charters.per_boat_note') }}</p>
            <p class="hint hint--safety">{{ t('public.charters.pricing.safety') }}</p>
          </section>

          <section v-if="charter.features?.length" class="card">
            <h2 class="section-title">{{ t('public.charters.sections.features') }}</h2>
            <ul class="occ occ--features">
              <li v-for="f in charter.features" :key="f">{{ t(`public.charters.features.items.${f}`) }}</li>
            </ul>
          </section>

          <section v-for="k in listSections" :key="k" class="card">
            <h2 class="section-title">{{ t(`public.charters.sections.${k}`) }}</h2>
            <ul class="bullets">
              <li v-for="(item, i) in charter[k]" :key="i">{{ item }}</li>
            </ul>
          </section>

          <section v-if="specs.length || charter.occasions?.length" class="card">
            <h2 class="section-title">{{ t('public.charters.sections.specs') }}</h2>
            <dl v-if="specs.length" class="specs">
              <template v-for="[k, v] in specs" :key="k">
                <dt>{{ t(`public.charters.fields.${k}`) }}</dt>
                <dd class="fw-value">{{ v }}</dd>
              </template>
            </dl>
            <ul v-if="charter.occasions?.length" class="occ">
              <li v-for="o in charter.occasions" :key="o">{{ t(`public.charters.occasions.${o}`) }}</li>
            </ul>
          </section>

          <section v-if="charter.faq?.length" class="card faq">
            <h2 class="section-title">{{ t('public.charters.sections.faq') }}</h2>
            <details v-for="(item, i) in charter.faq" :key="i" class="faq__item" :open="i === 0">
              <summary class="faq__q">{{ item.q }}</summary>
              <p class="faq__a">{{ item.a }}</p>
            </details>
          </section>

          <!-- Reviews: approved list, then the form (moderated before display) -->
          <section id="reviews" class="card">
            <h2 class="section-title">{{ t('public.charters.reviews.title') }}</h2>

            <div v-if="charter.reviews?.count > 0" class="reviews__header">
              <span class="reviews__average fw-value">{{ Number(charter.reviews.average).toFixed(1) }}</span>
              <span class="reviews__stars" aria-hidden="true">
                <span v-for="n in 5" :key="n" class="review-star" :style="{ color: n <= Math.round(charter.reviews.average) ? 'var(--warn)' : 'var(--line)' }">★</span>
              </span>
              <span class="reviews__count">{{ t('public.charters.reviews.count', { n: charter.reviews.count }, charter.reviews.count) }}</span>
            </div>

            <p v-if="!charter.reviews?.count" class="reviews__empty prose">{{ t('public.charters.reviews.empty') }}</p>

            <ul v-if="charter.reviews?.count > 0" class="reviews__list">
              <li v-for="(item, i) in visibleReviews" :key="i" class="review-item">
                <div class="review-item__meta">
                  <span class="review-item__stars" aria-hidden="true">
                    <span v-for="n in 5" :key="n" class="review-star" :style="{ color: n <= item.rating ? 'var(--warn)' : 'var(--line)' }">★</span>
                  </span>
                  <span class="review-item__author fw-value">{{ item.author_name }}</span>
                  <span class="review-item__date">{{ formatDate(item.date) }}</span>
                </div>
                <p class="review-item__body prose">{{ item.body }}</p>
                <div v-if="item.photos?.length" class="review-item__photos">
                  <a v-for="(ph, pi) in item.photos" :key="pi" :href="ph.full" target="_blank" rel="noopener">
                    <img :src="ph.thumb" :alt="t('public.reviews.photo_alt')" loading="lazy">
                  </a>
                </div>
              </li>
            </ul>

            <button
              v-if="charter.reviews?.count > 5 && !showAllReviews"
              class="reviews__more"
              type="button"
              @click="showAllReviews = true"
            >{{ t('public.charters.reviews.show_more') }}</button>

            <div class="reviews__form-wrap">
              <h3 class="reviews__form-title">{{ t('public.charters.reviews.write') }}</h3>

              <div v-if="reviewSubmitted" class="reviews__thanks">
                {{ t('public.charters.reviews.thanks') }}
              </div>

              <form v-else class="reviews__form" @submit.prevent="submitReview">
                <input
                  v-model="reviewForm.website"
                  type="text"
                  name="website"
                  tabindex="-1"
                  autocomplete="off"
                  class="reviews__honeypot"
                  aria-hidden="true"
                >

                <label class="review-field">
                  <span class="review-field__label">{{ t('public.charters.reviews.name') }}</span>
                  <input v-model="reviewForm.author_name" type="text" class="review-field__input" required maxlength="80">
                </label>

                <div class="review-field">
                  <span class="review-field__label">{{ t('public.charters.reviews.rating') }}</span>
                  <div class="star-picker" role="radiogroup" :aria-label="t('public.charters.reviews.rating')">
                    <button
                      v-for="n in 5"
                      :key="n"
                      type="button"
                      class="star-picker__btn"
                      :aria-label="t('public.reviews.star_label', { n }, n)"
                      :aria-pressed="reviewForm.rating === n"
                      :style="{ color: n <= reviewForm.rating ? 'var(--warn)' : 'var(--line)' }"
                      @click="reviewForm.rating = n"
                    >★</button>
                  </div>
                </div>

                <label class="review-field">
                  <span class="review-field__label">{{ t('public.charters.reviews.text') }}</span>
                  <textarea v-model="reviewForm.body" class="review-field__input review-field__textarea" required minlength="3" maxlength="2000" rows="4" />
                </label>

                <label class="review-field">
                  <span class="review-field__label">{{ t('public.charters.reviews.email') }}</span>
                  <input v-model="reviewForm.email" type="email" class="review-field__input" autocomplete="email">
                </label>

                <label class="review-field">
                  <span class="review-field__label">{{ t('public.reviews.add_photos') }}</span>
                  <input type="file" class="review-field__input review-field__file" accept="image/jpeg,image/png,image/webp" multiple @change="onPhotoChange">
                  <span class="review-field__hint">{{ t('public.reviews.photos_hint') }}</span>
                </label>
                <div v-if="photoPreviews.length" class="review-photos-preview">
                  <img v-for="(src, i) in photoPreviews" :key="i" :src="src" alt="">
                </div>

                <button type="submit" class="reviews__submit" :disabled="reviewForm.processing">
                  {{ t('public.charters.reviews.submit') }}
                </button>
              </form>
            </div>
          </section>
        </div>

        <aside class="detail__aside">
          <div id="request-to-book" class="plan-card">
            <h2 class="section-title">{{ t('public.charters.booking.title') }}</h2>
            <p class="plan-note">{{ t('public.charters.booking.note') }}</p>
            <label class="plan-row">
              <span class="plan-label">{{ t('public.charters.booking.format') }}</span>
              <select v-model="requestFormat" class="plan-input">
                <option v-for="f in formatOptions" :key="f.key" :value="f.key">
                  {{ t(`public.charters.formats.${f.key}`) }}<template v-if="f.price && !charter.price_on_request"> · {{ f.price }}</template>
                </option>
              </select>
            </label>
            <label class="plan-row">
              <span class="plan-label">{{ t('public.charters.booking.date') }}</span>
              <DateField v-model="requestDate" :min="today" class="plan-input" :placeholder="t('public.pick_date')" />
            </label>
            <label v-if="isOvernight" class="plan-row">
              <span class="plan-label">{{ t('public.charters.booking.end_date') }}</span>
              <DateField v-model="requestEnd" :min="requestDate || today" class="plan-input" :placeholder="t('public.pick_date')" />
            </label>
            <label class="plan-row">
              <span class="plan-label">{{ t('public.charters.booking.guests') }}</span>
              <input v-model.number="requestGuests" type="number" min="1" :max="charter.capacity" class="plan-input plan-input--short">
            </label>
            <p v-if="charter.extra_guest_price && requestGuests > (charter.covers_guests || charter.capacity)" class="plan-extra">
              {{ t('public.charters.pricing.extra_note', { n: requestGuests - (charter.covers_guests || charter.capacity), price: charter.extra_guest_price }) }}
            </p>
            <p v-if="totalDisplay" class="plan-total">{{ t('public.charters.booking.total') }} <span class="fw-value">{{ totalDisplay }}</span></p>
            <p v-if="selectedBusy" class="plan-busy">{{ t('public.charters.availability.busy') }}</p>
            <p v-else-if="availability.live" class="plan-live">{{ t('public.charters.availability.live') }}<template v-if="mounted && nextBooked.length">{{ ' ' + t('public.charters.availability.next_booked', { dates: nextBooked.join(', ') }) }}</template></p>
            <h3 class="plan-subtitle">{{ t('public.booking_request.title') }}</h3>
            <BookingRequestForm
              bookable-type="charter"
              :bookable-id="charter.id"
              :details="details"
              :disabled="!canRequest || selectedBusy"
            />
            <p v-if="chatCtas.length" class="plan-divider">{{ t('public.booking_request.or_chat') }}</p>
            <div v-if="chatCtas.length" class="plan-ctas">
              <LuxButton v-for="cta in chatCtas" :key="cta.platform" :href="cta.url" variant="line" tone="light" :glyph="cta.platform" block target="_blank" rel="noopener">
                {{ t('public.charters.booking.cta_via', { platform: cta.name }) }}
              </LuxButton>
            </div>
            <PaymentMethods inline />
          </div>

          <CheckoutCard
            v-if="checkout_enabled && !charter.price_on_request"
            bookable-type="charter"
            :bookable-id="charter.id"
            :details="checkoutDetails"
            :disabled="!quotable || selectedBusy"
            :quote="quote"
            :quote-loading="quoteLoading"
          />

          <div class="trust-card">
            <h2 class="section-title">{{ t('public.charters.trust.title') }}</h2>
            <ul class="trust-list">
              <li>{{ t('public.charters.trust.licensed') }}</li>
              <li>{{ t('public.charters.trust.deposit') }}</li>
              <li>{{ t('public.charters.trust.local') }}</li>
            </ul>
          </div>
        </aside>
      </div>

      <section v-if="charter.similar?.length" class="similar">
        <h2 class="similar__title">{{ t('public.charters.sections.similar') }}</h2>
        <ul class="similar__grid">
          <li v-for="s in charter.similar" :key="s.id">
            <ProductCard kind="charter" :item="s" :href="`${base_path}/${s.id}`" />
          </li>
        </ul>
      </section>
    </div>
  </section>

  <MobileBar target="request-to-book" :value="charter.price_on_request ? t('public.vip.price_on_request') : `${t('public.charters.from')} ${charter.from_price}`" :sub="charter.price_on_request ? '' : t('public.charters.per_boat')" :cta="t('public.charters.booking.title')" />
</template>

<style scoped>
.bhero-back { display: inline-block; margin-bottom: 1rem; color: inherit; opacity: 0.85; text-decoration: none; font-size: 0.95rem; }
.bhero-back:hover { opacity: 1; }
.bhero-lede { margin: 0.9rem 0 0; font-size: clamp(1.05rem, 2vw, 1.25rem); line-height: 1.5; max-width: 52ch; opacity: 0.94; }
.bhero-vip { display: inline-flex; align-items: center; margin: 1rem 0.4rem 0 0; padding: 0.35rem 0.75rem; border-radius: 999px; font-size: 0.85rem; font-weight: 700; background: var(--ink); color: var(--gold, var(--warn)); border: 1px solid var(--gold, var(--warn)); }
.bhero-sails { display: flex; flex-wrap: wrap; gap: 0.45rem; align-items: center; margin: 0.9rem 0 0; }
.bhero-sails__label { font-size: 0.72rem; letter-spacing: 0.1em; text-transform: uppercase; font-weight: 700; opacity: 0.8; margin-right: 0.2rem; }
.bhero-sails__chip { display: inline-flex; padding: 0.35rem 0.8rem; border-radius: 999px; border: 1px solid color-mix(in oklch, currentColor 45%, transparent); color: inherit; font-size: 0.82rem; font-weight: 600; text-decoration: none; }
.bhero-sails__chip:hover { border-color: currentColor; }
.bhero-vip--premium { color: var(--sky, #E4F1F7); border-color: var(--sky, #E4F1F7); }
.bhero-ctas { display: flex; flex-wrap: wrap; gap: 0.6rem; margin-top: 1.5rem; }

.body { background: var(--bg); }
.body__inner { max-width: 96rem; margin: 0 auto; padding: clamp(2rem, 4vw, 3rem) clamp(1.25rem, 4vw, 3rem) 4rem; }
@media (max-width: 879px) { .body__inner { padding-bottom: 6.5rem; } }

/* Stat strip: from-price · length · guests · cabins. */
.stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.75rem; margin: 1.25rem 0 0; }
.stat { background: var(--surface); border: 1px solid var(--line); border-radius: 0.9rem; padding: 0.85rem 1rem; }
.stat__l { margin: 0; font-size: 0.72rem; letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted); font-weight: 700; }
.stat__v { margin: 0.2rem 0 0; font-family: var(--font-display, var(--display)); font-size: 1.35rem; line-height: 1.15; color: var(--ink); }
.stat__v small { font-family: var(--body, inherit); font-size: 0.78rem; color: var(--muted); font-weight: 400; }
@media (max-width: 48rem) { .stats { grid-template-columns: 1fr 1fr; gap: 0.6rem; } .stat { padding: 0.7rem 0.85rem; } .stat__v { font-size: 1.15rem; } }
.trust { margin-top: 1rem; padding: 0.9rem 0; border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); }

.detail { display: grid; grid-template-columns: 1fr; gap: 1.5rem; margin-top: 1.5rem; }
@media (min-width: 880px) { .detail { grid-template-columns: 1.6fr 0.9fr; gap: 2rem; align-items: start; } }
.detail__content { display: grid; gap: 1rem; }
/* The request card stays in view on desktop; taller than the viewport, it
   scrolls inside its own column rather than sliding away. */
@media (min-width: 880px) { .detail__aside { position: sticky; top: 1.25rem; max-height: calc(100vh - 2.5rem); overflow-y: auto; scrollbar-width: thin; } }
.card, .plan-card, .trust-card { background: var(--surface); border: 1px solid var(--line); border-radius: 1rem; padding: 1.4rem; }
.trust-card { margin-top: 1.25rem; }
.section-title { font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); margin: 0 0 0.75rem; }
.prose { color: var(--inkSoft); line-height: 1.6; white-space: pre-wrap; margin: 0; }
.hint { margin: 0.75rem 0 0; font-size: 0.85rem; color: var(--muted); }
.hint--safety { color: var(--inkSoft); }
.mono { font-family: var(--font-mono, var(--mono)); font-size: 0.75rem; letter-spacing: 0.04em; text-transform: uppercase; color: var(--muted); }

.formats { width: 100%; border-collapse: collapse; }
.formats th, .formats td { padding: 0.75rem 0.5rem; text-align: left; border-bottom: 1px solid var(--line); vertical-align: top; }
.formats tbody th { color: var(--ink); font-weight: 600; font-family: var(--font-display, var(--display)); font-size: 1.2rem; }
.formats__hours { display: block; margin-top: 0.15rem; font-weight: 400; }
.formats__price { text-align: right; }
.formats__price .fw-value { display: block; font-family: var(--font-display, var(--display)); font-size: 1.35rem; color: var(--ink); }
.formats__cover { display: block; margin-top: 0.15rem; font-size: 0.8rem; color: var(--muted); }
.formats tbody tr:last-child th, .formats tbody tr:last-child td { border-bottom: none; }
.incl { margin: 0.85rem 0 0; font-size: 0.92rem; line-height: 1.5; color: var(--inkSoft); }
.incl b { color: var(--ink); font-weight: 700; }

.bullets { margin: 0; padding: 0; list-style: none; display: grid; gap: 0.5rem; }
.bullets li { position: relative; padding-left: 1.4rem; color: var(--inkSoft); line-height: 1.5; }
.bullets li::before { content: ''; position: absolute; left: 0.2rem; top: 0.6rem; width: 0.45rem; height: 0.45rem; border-radius: 50%; background: var(--second); }

.specs { display: grid; grid-template-columns: auto 1fr; gap: 0.5rem 1rem; margin: 0; }
.specs dt { color: var(--muted); font-size: 0.85rem; }
.specs dd { margin: 0; color: var(--ink); }
.occ { list-style: none; margin: 1rem 0 0; padding: 0; display: flex; flex-wrap: wrap; gap: 0.4rem; }
.occ li { padding: 0.3rem 0.7rem; border-radius: 999px; background: var(--sky, var(--secondSoft)); color: var(--ink); font-size: 0.8rem; font-weight: 600; }
.occ--features { margin-top: 0; }

.faq__item { border-top: 1px solid var(--line); padding: 0.7rem 0; }
.faq__item:first-of-type { border-top: 0; padding-top: 0; }
.faq__q { cursor: pointer; font-weight: 600; color: var(--ink); line-height: 1.4; list-style: none; position: relative; padding-right: 1.6rem; }
.faq__q::-webkit-details-marker { display: none; }
.faq__q::after { content: '+'; position: absolute; right: 0.2rem; top: 0; color: var(--muted); font-weight: 400; }
.faq__item[open] .faq__q::after { content: '–'; }
.faq__a { margin: 0.5rem 0 0; color: var(--inkSoft); line-height: 1.55; }

.plan-note { margin: 0 0 0.85rem; color: var(--inkSoft); font-size: 0.9rem; line-height: 1.5; }
.plan-row { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-top: 0.7rem; }
.plan-label { color: var(--inkSoft); font-size: 0.95rem; }
.plan-input { border: 1px solid var(--line); border-radius: var(--radius-input, 0.5rem); background: var(--bg); color: var(--ink); padding: 0.45rem 0.6rem; font: inherit; max-width: 60%; color-scheme: light; }
.plan-input--short { max-width: 6rem; }
.plan-extra { margin: 0.6rem 0 0; font-size: 0.82rem; color: var(--inkSoft); }
.plan-live  { margin: 0.6rem 0 0; font-size: 0.8rem; color: var(--muted); line-height: 1.45; }
.plan-busy  { margin: 0.6rem 0 0; font-size: 0.85rem; color: var(--danger); line-height: 1.45; }
.plan-subtitle { margin: 1.1rem 0 0; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); }
.plan-divider { margin: 0.9rem 0 0; text-align: center; font-size: 0.8rem; color: var(--muted); }
.plan-ctas { margin-top: 1rem; display: grid; gap: 0.5rem; }

.trust-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.6rem; }
.trust-list li { position: relative; padding-left: 1.6rem; color: var(--inkSoft); line-height: 1.4; }
.trust-list li::before { content: "✓"; position: absolute; left: 0; top: 0; color: var(--ok); font-weight: 700; }
.bhero-note { margin: 0.9rem 0 0; font-size: 0.95rem; line-height: 1.5; opacity: 0.92; max-width: 46ch; }

.similar { --card-accent: var(--second); margin-top: clamp(2rem, 4vw, 3rem); padding-top: clamp(1.5rem, 3vw, 2rem); border-top: 1px solid color-mix(in oklch, var(--gold, var(--warn)) 40%, var(--line)); }
.similar__title { margin: 0 0 1rem; font-family: var(--display); font-weight: 600; font-size: 1.45rem; color: var(--ink); }
.similar__grid { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr)); gap: 1rem; }

/* Phone price bar. Hidden on desktop and while the request card is on screen. */

.plan-total { margin: 0.75rem 0 0; font-size: 0.95rem; color: var(--ink); }
.plan-total .fw-value { font-size: 1.1rem; }

/* Reviews — the same block the experience and vehicle pages carry. */
.reviews__header { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1rem; }
.reviews__average { font-size: 1.4rem; }
.reviews__stars { display: inline-flex; gap: 0.1rem; font-size: 1.1rem; }
.reviews__count { color: var(--muted); font-size: 0.9rem; }
.review-star { transition: color 0.1s; }
.reviews__empty { margin: 0.5rem 0 1rem; }
.reviews__list { list-style: none; margin: 0 0 1rem; padding: 0; display: grid; gap: 1rem; }
.review-item { padding: 0.9rem; background: var(--surfaceAlt); border: 1px solid var(--line); border-radius: var(--radius-table, 0.75rem); }
.review-item__meta { display: flex; align-items: center; flex-wrap: wrap; gap: 0.4rem 0.75rem; margin-bottom: 0.4rem; }
.review-item__stars { display: inline-flex; gap: 0.05rem; font-size: 0.95rem; }
.review-item__author { font-size: 0.95rem; }
.review-item__date { font-size: 0.8rem; color: var(--muted); margin-left: auto; }
.review-item__body { margin: 0; font-size: 0.95rem; }
.review-item__verified {
  display: inline-flex; align-items: center;
  padding: 0.1rem 0.5rem; border-radius: 999px;
  font-size: 0.72rem; font-weight: 700;
  color: var(--ok);
  background: color-mix(in oklch, var(--ok) 12%, var(--surface));
}
.review-item__photos { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 0.5rem; }
.review-item__photos img { width: 72px; height: 72px; object-fit: cover; border-radius: 0.5rem; border: 1px solid var(--line); }
.reviews__more { margin-top: 0.25rem; background: none; border: 1px solid var(--line); border-radius: var(--radius-pill, 999px); padding: 0.45rem 1rem; cursor: pointer; color: var(--ink); font: inherit; font-size: 0.9rem; transition: border-color 150ms ease, color 150ms ease, transform 150ms ease; }
.reviews__more:hover { border-color: var(--accent); color: var(--accent); transform: translateY(-2px); }
.reviews__form-wrap { margin-top: 1.5rem; padding-top: 1.25rem; border-top: 1px solid var(--line); }
.reviews__form-title { font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); margin: 0 0 1rem; }
.reviews__thanks { padding: 0.85rem 1rem; background: var(--surfaceAlt); border: 1px solid var(--line); border-radius: var(--radius-table, 0.75rem); color: var(--ok); font-weight: 600; }
.reviews__form { display: grid; gap: 0.85rem; }
.reviews__honeypot { position: absolute; left: -9999px; width: 1px; height: 1px; overflow: hidden; opacity: 0; pointer-events: none; }
.review-field { display: grid; gap: 0.3rem; }
.review-field__label { font-size: 0.875rem; color: var(--muted); }
.review-field__input { border: 1px solid var(--line); border-radius: var(--radius-input, 0.5rem); background: var(--bg); color: var(--ink); padding: 0.45rem 0.65rem; font: inherit; font-size: 0.95rem; width: 100%; box-sizing: border-box; }
.review-field__input:focus { outline: 2px solid var(--accent); outline-offset: 1px; }
.review-field__textarea { resize: vertical; min-height: 6rem; }
.review-field__file { padding: 0.45rem; }
.review-field__hint { font-size: 0.78rem; color: var(--muted); margin-top: 0.25rem; }
.review-photos-preview { display: flex; flex-wrap: wrap; gap: 0.4rem; }
.review-photos-preview img { width: 64px; height: 64px; object-fit: cover; border-radius: 0.5rem; border: 1px solid var(--line); }
.star-picker { display: inline-flex; gap: 0.15rem; }
.star-picker__btn { background: none; border: none; font-size: 1.5rem; cursor: pointer; padding: 0.1rem 0.05rem; line-height: 1; transition: color 0.1s, transform 0.1s; }
.star-picker__btn:hover { transform: scale(1.15); }
.reviews__submit { align-self: start; display: inline-flex; align-items: center; justify-content: center; padding: 0.95rem 1.6rem; border-radius: 999px; cursor: pointer; appearance: none; font-family: var(--font-body, var(--body)); font-size: 0.78rem; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; line-height: 1; background: var(--night, var(--ink)); color: var(--foam, #ECF3F6); border: 1px solid var(--night, var(--ink)); box-shadow: 0 16px 34px -20px color-mix(in oklch, var(--ink) 60%, transparent), inset 0 0 0 1px color-mix(in oklch, var(--gold, #D8B46B) 40%, transparent); transition: transform 160ms ease, background 160ms ease; }
.reviews__submit:hover:not(:disabled) { transform: translateY(-1px); background: var(--night2, var(--ink)); border-color: var(--night2, var(--ink)); }
.reviews__submit:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
